/**
 * HistoricalStore — data-access for closed-out estimate-vs-actual records that
 * feed estimation accuracy aggregation and the AI estimate's historical signal.
 *
 * Strict layering: talks to the API only through the {@link EngineeringVectorApi}
 * facade (`api.documents<HistoricalEstimate>('historicalEstimates')`).
 *
 * Persistence model: each record is one document with id `hist_<uuid>`. All
 * fields are mirrored onto the DDB pointer row (numbers as strings, string
 * arrays as JSON) so accuracy roll-ups render directly from pointer rows
 * WITHOUT hydrating S3 bodies.
 *
 * Scoping: {@link listByRoom} uses a roomId-filtered listMetaInfo query and
 * {@link listByService} narrows further to a service, each with a defensive
 * client-side re-filter.
 */

import type { EngineeringVectorApi } from '../../../api';
import type { DocumentStore } from '../../../api';
import type { StringMap } from '../../../api';
import type { ListMetaInfoQuery } from '../../../api';
import type {
  HistoricalEstimate,
  HistoricalEstimateDraft,
} from '../models/types';
import { historicalDocId } from '../models/sprintboardModel';

export const HISTORICAL_ESTIMATES_COLLECTION = 'historicalEstimates';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  roomId: 'roomId',
  taskId: 'taskId',
  taskType: 'taskType',
  originalEstimateDays: 'originalEstimateDays',
  finalEstimateDays: 'finalEstimateDays',
  actualEffortDays: 'actualEffortDays',
  confidence: 'confidence',
  serviceId: 'serviceId',
  complexityFactors: 'complexityFactors',
  extraWork: 'extraWork',
  recordedAt: 'recordedAt',
  deleted: 'deleted',
} as const;

export class HistoricalStore {
  private readonly docs: DocumentStore<HistoricalEstimate>;

  constructor(api: EngineeringVectorApi) {
    this.docs = api.documents<HistoricalEstimate>(
      HISTORICAL_ESTIMATES_COLLECTION,
    );
  }

  private num(v: number | undefined): string {
    return v != null && Number.isFinite(v) ? String(v) : '0';
  }

  private parseNum(raw: string | undefined): number {
    if (raw == null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  private parseStringArray(raw: string | undefined): string[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((x) => typeof x === 'string')
        : [];
    } catch {
      return [];
    }
  }

  private indexAttributes(h: HistoricalEstimate): StringMap {
    return {
      [ROW.roomId]: h.roomId,
      [ROW.taskId]: h.taskId,
      [ROW.taskType]: h.taskType ?? '',
      [ROW.originalEstimateDays]: this.num(h.originalEstimateDays),
      [ROW.finalEstimateDays]: this.num(h.finalEstimateDays),
      [ROW.actualEffortDays]: this.num(h.actualEffortDays),
      [ROW.confidence]: this.num(h.confidence),
      [ROW.serviceId]: h.serviceId ?? '',
      [ROW.complexityFactors]: JSON.stringify(h.complexityFactors ?? []),
      [ROW.extraWork]: JSON.stringify(h.extraWork ?? []),
      [ROW.recordedAt]: h.recordedAt ?? '',
      [ROW.deleted]: h.deleted ? 'true' : 'false',
    };
  }

  private toRecord(row: StringMap): HistoricalEstimate {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      roomId: row[ROW.roomId] ?? '',
      taskId: row[ROW.taskId] ?? '',
      taskType: row[ROW.taskType] ?? '',
      originalEstimateDays: this.parseNum(row[ROW.originalEstimateDays]),
      finalEstimateDays: this.parseNum(row[ROW.finalEstimateDays]),
      actualEffortDays: this.parseNum(row[ROW.actualEffortDays]),
      confidence: this.parseNum(row[ROW.confidence]),
      serviceId: row[ROW.serviceId] || undefined,
      complexityFactors: this.parseStringArray(row[ROW.complexityFactors]),
      extraWork: this.parseStringArray(row[ROW.extraWork]),
      recordedAt: row[ROW.recordedAt] ?? '',
      deleted: row[ROW.deleted] === 'true',
    };
  }

  /** Lists a room's non-deleted historical estimates. */
  async listByRoom(roomId: string): Promise<HistoricalEstimate[]> {
    if (!roomId) return [];
    const query: ListMetaInfoQuery = {
      filterExpression: '#roomId = :roomId AND #deleted <> :deleted',
      expressionAttributeNames: {
        '#roomId': ROW.roomId,
        '#deleted': ROW.deleted,
      },
      expressionAttributeValues: {
        ':roomId': roomId,
        ':deleted': 'true',
      },
    };
    const rows = await this.docs.list(query);
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === HISTORICAL_ESTIMATES_COLLECTION &&
          r[ROW.roomId] === roomId &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toRecord(r));
  }

  /**
   * Lists a room's historical estimates for a single service. Narrows the
   * room-scoped set client-side (serviceId is not the primary filter key).
   */
  async listByService(
    roomId: string,
    serviceId: string,
  ): Promise<HistoricalEstimate[]> {
    if (!serviceId) return [];
    const all = await this.listByRoom(roomId);
    return all.filter((r) => r.serviceId === serviceId);
  }

  /** Loads a single historical estimate document by id. */
  async get(id: string): Promise<HistoricalEstimate | undefined> {
    return this.docs.get(id);
  }

  /** Records a new historical estimate in a room. Seeds id/roomId/recordedAt. */
  async record(
    roomId: string,
    draft: HistoricalEstimateDraft,
  ): Promise<HistoricalEstimate> {
    const record: HistoricalEstimate = {
      id: historicalDocId(),
      roomId,
      taskId: draft.taskId,
      taskType: draft.taskType,
      originalEstimateDays: draft.originalEstimateDays,
      finalEstimateDays: draft.finalEstimateDays,
      actualEffortDays: draft.actualEffortDays,
      confidence: draft.confidence,
      serviceId: draft.serviceId?.trim() || undefined,
      complexityFactors: draft.complexityFactors ?? [],
      extraWork: draft.extraWork ?? [],
      recordedAt: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.save(record.id, record, this.indexAttributes(record));
    return record;
  }

  /** Soft-deletes a historical estimate. */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: HistoricalEstimate = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }
}
